import { useEffect, useMemo, useRef, useState } from "react";

import { useGlobalDataDownloadUrl, useGlobalDataText } from "../api/hooks/useGlobalData";
import { useUploadDownloadUrl } from "../api/hooks/useUploadActions";
import { useUploadExtractedText } from "../api/hooks/useUploadExtractedText";
import { locateValueInText, pageAtOffset, type TextMatch } from "./textExcerpt";
import styles from "./SourceTextModal.module.css";

interface Props {
  // The document: a project upload, or a global reference file. Exactly one.
  uploadId?: string;
  globalDataId?: string;
  // Whether the upload kept a source-text layer. When true the "Extracted text"
  // tab is offered; the "Source document" tab is always available (any upload
  // can be rendered/downloaded from its original bytes).
  hasSourceText: boolean;
  // The suggested value + verbatim quote, used to locate + highlight the relevant
  // passage in the extracted text. Several quotes are tried in turn, the first found
  // opening the viewer.
  value?: string | null;
  evidence?: string | readonly string[] | null;
  // Opened from a citation, which may name the document without quoting it.
  cited?: boolean;
  // Filename - shown under the heading + used as the download name.
  sourceLabel?: string | null;
  // The upload's MIME type - decides whether the "Source document" tab can render
  // the file inline (PDF / image) or must offer a download instead (Word / Excel /
  // unknown), so switching to the tab never triggers a silent browser download.
  contentType?: string | null;
  // Where the original sits in Drive, when it came from Drive: shown under the name
  // with a link, so a reader can tell which version of a report is meant.
  driveFileId?: string | null;
  drivePath?: string | null;
  onClose: () => void;
}

type Tab = "text" | "document";

// Whether the browser can render this file inline in an <iframe> without falling
// back to a download. PDFs + images render; Office docs (docx/xlsx) and unknown
// (octet-stream) types download when navigated to, so we must NOT iframe them.
const canRenderInline = (
  contentType?: string | null,
  fileName?: string | null,
): boolean => {
  const ct = (contentType ?? "").toLowerCase();
  if (ct === "application/pdf" || ct.startsWith("image/")) return true;
  if (ct) return false; // a known, non-inline content type (docx/xlsx/octet-stream)
  // No content type - fall back to the filename extension.
  return /\.(pdf|png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName ?? "");
};

const isPdf = (contentType?: string | null, fileName?: string | null): boolean => {
  const ct = (contentType ?? "").toLowerCase();
  return ct ? ct === "application/pdf" : /\.pdf$/i.test(fileName ?? "");
};

// A modal that shows the complete source for one upload so an operator can verify
// a value without leaving the tracker. Two tabs:
//  - "Extracted text": the persisted source text with the value located +
//    highlighted + scrolled into view (only when the upload kept text).
//  - "Source document": the original PDF/image rendered inline, with a download
//    button, a PDF opened at the page the located passage is on. Always available
//    - a vision-read drawing has no text layer, so this is the only way to see it.
export const SourceTextModal = ({
  uploadId,
  globalDataId,
  hasSourceText,
  value,
  evidence,
  cited = false,
  sourceLabel,
  contentType,
  driveFileId,
  drivePath,
  onClose,
}: Props): JSX.Element => {
  const inlineRenderable = canRenderInline(contentType, sourceLabel);
  const pdf = isPdf(contentType, sourceLabel);
  // Keyed on the words, since a caller's array is a fresh one each render.
  const quoteKey = typeof evidence === "string" ? evidence : (evidence ?? []).join("\u0000");
  const quotes = useMemo(() => quoteKey.split("\u0000").filter((q) => q.trim()), [quoteKey]);
  const canLocate = hasSourceText && Boolean(value?.trim() || quotes.length);
  const ref = useRef<HTMLDialogElement | null>(null);
  const markRef = useRef<HTMLElement | null>(null);
  // Which tab opens first: the highlighted passage when the citation's words are
  // found in the text, otherwise the original. A Word / Excel file only shows
  // "can't be previewed" on the document tab, so its text always opens first.
  // Null while the text loads to decide.
  const [tab, setTab] = useState<Tab | null>(
    !hasSourceText ? "document" : !inlineRenderable ? "text" : canLocate ? null : "document",
  );

  // The text is fetched for the text tab, and for the document tab when it is
  // needed to find the page the passage is on.
  const wantText = hasSourceText && (tab !== "document" || canLocate);
  const uploadText = useUploadExtractedText(wantText ? uploadId : undefined);
  const globalText = useGlobalDataText(wantText ? globalDataId : undefined);
  const textQuery = globalDataId ? globalText : uploadText;
  const textData = textQuery.data;
  const textLoading = textQuery.isLoading;
  const textError = textQuery.isError;
  const textErrorDetail = textQuery.error;
  // Document tab: a presigned URL to the original file (resolved once opened).
  const uploadDownload = useUploadDownloadUrl();
  const globalDownload = useGlobalDataDownloadUrl();
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  useEffect(() => {
    if (tab !== "document" || docUrl || docError) return;
    let cancelled = false;
    (globalDataId
      ? globalDownload.mutateAsync(globalDataId)
      : uploadDownload.mutateAsync(uploadId ?? "")
    )
      .then((resp) => {
        if (!cancelled) setDocUrl(resp.url);
      })
      .catch((e: unknown) => {
        if (!cancelled) setDocError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, uploadId, globalDataId]);

  const text = textData?.text ?? "";
  const match = useMemo(
    () =>
      text
        ? (quotes.length ? quotes : [null]).reduce<TextMatch | null>(
            (found, quote) => found ?? locateValueInText(text, { value, evidence: quote }),
            null,
          )
        : null,
    [text, value, quotes],
  );
  const matchStart = match?.start ?? null;
  const matchEnd = match?.end ?? null;
  const page = pdf && match ? pageAtOffset(text, match.start) : null;
  // Hold the PDF until the page is known, so the frame loads once at that page.
  const waitingForPage = pdf && canLocate && textLoading;

  useEffect(() => {
    if (tab !== null || textLoading) return;
    setTab(match ? "text" : "document");
  }, [tab, textLoading, match]);

  useEffect(() => {
    if (markRef.current) markRef.current.scrollIntoView({ block: "center" });
  }, [matchStart, matchEnd, text, tab]);

  // Force a real download (S3 is cross-origin, so the <a download> attribute is
  // ignored) by fetching the bytes and saving the blob.
  const doDownload = async (): Promise<void> => {
    if (!docUrl) return;
    setDownloading(true);
    try {
      const resp = await fetch(docUrl);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = sourceLabel || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.alert(
        `Couldn't download: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setDownloading(false);
    }
  };

  const renderText = (): JSX.Element => {
    if (textLoading)
      return <p className={styles.status}>Loading extracted text…</p>;
    if (textError)
      return (
        <div className={styles.error}>
          Could not load the extracted text (
          {textErrorDetail instanceof Error
            ? textErrorDetail.message
            : "the request failed"}
          ). Try again in a moment.
        </div>
      );
    if (!text)
      return (
        <p className={styles.status}>No source text was kept for this file.</p>
      );
    if (!match)
      return (
        <>
          {(value || quotes.length > 0 || cited) && (
            <p className={styles.subhead}>
              {value
                ? "The value couldn't be located in the text (the extractor may have paraphrased) - showing the full text."
                : quotes.length
                  ? "The quoted words couldn't be located in this text - showing the full text."
                  : "This citation names the document without quoting it - showing the full text."}
            </p>
          )}
          <pre className={styles.pre}>{text}</pre>
        </>
      );
    return (
      <pre className={styles.pre}>
        {text.slice(0, match.start)}
        <mark className={styles.mark} ref={markRef}>
          {text.slice(match.start, match.end)}
        </mark>
        {text.slice(match.end)}
      </pre>
    );
  };

  const renderDocument = (): JSX.Element => {
    if (docError)
      return (
        <div className={styles.error}>
          Could not load the document: {docError}
        </div>
      );
    if (!docUrl || waitingForPage)
      return <p className={styles.status}>Loading document…</p>;
    // A fragment is never sent to S3, so it leaves the presigned signature intact.
    const src = page ? `${docUrl}#page=${page}` : docUrl;
    return (
      <>
        {page && (
          <p className={styles.subhead}>
            Opened at page {page}, where the quoted passage is. It is highlighted on
            the Extracted text tab.
          </p>
        )}
        <div className={styles.docActions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={doDownload}
            disabled={downloading}
          >
            {downloading ? "Downloading…" : "Download file"}
          </button>
          {inlineRenderable && (
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.docLink}
            >
              Open in a new tab ↗
            </a>
          )}
        </div>
        {inlineRenderable ? (
          // Only PDFs / images render in an <iframe>. Pointing an iframe at a Word
          // or Excel file makes the browser DOWNLOAD it on tab-switch, which is
          // exactly what we're avoiding - so those get the message below instead.
          <iframe
            className={styles.docFrame}
            src={src}
            title={sourceLabel ?? "Document"}
          />
        ) : (
          <p className={styles.status}>
            This file type can&apos;t be previewed in the browser. Use{" "}
            <strong>Download file</strong> above to open it
            {sourceLabel ? ` (${sourceLabel})` : ""}.
          </p>
        )}
      </>
    );
  };

  return (
    <dialog
      ref={ref}
      // The embedded document needs more room to read than the text view, so the
      // modal widens on the "Source document" tab.
      className={`${styles.dialog} ${tab === "document" ? styles.wide : ""}`}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <div className={styles.body}>
        <div className={styles.headerRow}>
          <div>
            <h2 className={styles.heading}>Complete source</h2>
            {sourceLabel ? (
              <p className={styles.subhead}>{sourceLabel}</p>
            ) : null}
            {(drivePath || driveFileId) && (
              <p className={styles.subhead}>
                {drivePath && <>In Drive: {drivePath} </>}
                {driveFileId && (
                  <a
                    href={`https://drive.google.com/file/d/${encodeURIComponent(driveFileId)}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.docLink}
                  >
                    Open in Drive ↗
                  </a>
                )}
              </p>
            )}
          </div>
          <button type="button" className={styles.btnClose} onClick={onClose}>
            Close
          </button>
        </div>
        <div className={styles.tabs}>
          {hasSourceText && (
            <button
              type="button"
              className={tab === "text" ? styles.tabActive : styles.tab}
              onClick={() => setTab("text")}
            >
              Extracted text
            </button>
          )}
          <button
            type="button"
            className={tab === "document" ? styles.tabActive : styles.tab}
            onClick={() => setTab("document")}
          >
            Source document
          </button>
        </div>
        {tab === null ? (
          <p className={styles.status}>Finding the quoted passage…</p>
        ) : tab === "text" ? (
          renderText()
        ) : (
          renderDocument()
        )}
        <div className={styles.actions}>
          <button type="button" className={styles.btnClose} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default SourceTextModal;
